import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGlobalDialogs } from '../contexts/GlobalDialogsContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import GlobalSearchBox from './GlobalSearchBox';
import BrandLogo from './BrandLogo';
import SubscriptionBanner from './SubscriptionBanner';
import TrialCountdownBadge from './TrialCountdownBadge';
import {
  LayoutDashboard, Users, FileText, Calendar, CheckSquare, DollarSign,
  FileSpreadsheet, Bell, Settings, LogOut, Menu, UserCircle, TrendingUp,
  Plus, Megaphone, ChevronDown, ChevronRight, Sparkles, CreditCard, Shield, LifeBuoy,
  FileWarning,
} from 'lucide-react';
import SupportDialog from './SupportDialog';
import { toast } from 'sonner';

// Category structure shared by Products & Renewals sub-menus.
const PRODUCT_CATEGORIES = [
  { key: 'insurance:life',       label: 'Insurance · Life' },
  { key: 'insurance:general',    label: 'Insurance · General' },
  { key: 'investment',           label: 'Investment' },
  { key: 'loans',                label: 'Loans' },
];

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { openAddClient, openAddProduct } = useGlobalDialogs();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);  // Round 59 · Item 7 — top-right Support dialog
  const [openMenu, setOpenMenu] = useState(() => {
    if (location.pathname === '/policies') return 'products';
    if (location.pathname === '/renewals') return 'renewals';
    return null;
  });

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const topItems = [
    { name: 'Dashboard',    path: '/dashboard',    icon: LayoutDashboard },
    { name: 'Leads',        path: '/leads',        icon: TrendingUp },
    { name: 'Lost Leads',   path: '/lost-leads',   icon: TrendingUp },
    { name: 'Clients',      path: '/clients',      icon: Users },
  ];
  const submenus = [
    { key: 'products', name: 'Products', path: '/policies', icon: FileText },
    { key: 'renewals', name: 'Renewals', path: '/renewals', icon: Calendar },
  ];
  const bottomItems = [
    { name: 'Tasks',         path: '/tasks',         icon: CheckSquare },
    { name: 'Claims',        path: '/claims',        icon: FileWarning },
    { name: 'Commissions',   path: '/commissions',   icon: DollarSign },
    { name: 'Campaigns',     path: '/campaigns',     icon: Megaphone },
    { name: 'Quotations',    path: '/quotations',    icon: FileSpreadsheet },
    { name: 'Reports',       path: '/reports',       icon: FileSpreadsheet },
    { name: 'Announcements', path: '/announcements', icon: Sparkles },
    { name: 'Notifications', path: '/notifications', icon: Bell },
    { name: 'Billing',       path: '/billing',       icon: CreditCard },
    { name: 'Settings',      path: '/settings',      icon: Settings },
    // Admin-only link appended below via role check.
  ];
  if (user?.role === 'admin') {
    bottomItems.push({ name: 'Admin', path: '/admin', icon: Shield });
  }
  const allItems = [...topItems, ...submenus.map(s => ({ name: s.name, path: s.path, icon: s.icon })), ...bottomItems];
  const pageTitle = (allItems.find((n) => n.path === location.pathname)?.name) || 'InsureGrowth';

  const NavLinkItem = ({ item, onClick }) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    return (
      <Link
        to={item.path}
        onClick={onClick}
        data-testid={`nav-${item.name.toLowerCase()}`}
        className={`flex items-center px-4 py-3 rounded-lg font-medium transition-colors duration-200 ${
          isActive ? 'bg-blue-800 text-white' : 'text-slate-700 hover:bg-slate-100'
        }`}
      >
        <Icon className="h-5 w-5 mr-3" />
        {item.name}
      </Link>
    );
  };

  const SubMenu = ({ item }) => {
    const Icon = item.icon;
    const expanded = openMenu === item.key;
    const isActive = location.pathname === item.path;
    const currentCat = searchParams.get('category') || 'all';
    return (
      <div>
        {/* Split control — LEFT half navigates to the page (shows ALL records
            with ?category=all), RIGHT half toggles the category sub-menu.
            Fixes the recurring "menu requires sub-selection" complaint. */}
        <div
          className={`w-full flex items-stretch rounded-lg font-medium overflow-hidden ${
            isActive ? 'bg-blue-800 text-white' : 'text-slate-700 hover:bg-slate-100'
          }`}
        >
          <Link
            to={item.path}
            onClick={() => setMobileOpen(false)}
            data-testid={`nav-${item.name.toLowerCase()}`}
            className="flex-1 flex items-center px-4 py-3 transition-colors duration-200"
          >
            <Icon className="h-5 w-5 mr-3" />{item.name}
          </Link>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); setOpenMenu(expanded ? null : item.key); }}
            data-testid={`nav-${item.name.toLowerCase()}-toggle`}
            aria-label={`Toggle ${item.name} categories`}
            className={`px-3 border-l ${isActive ? 'border-blue-700' : 'border-slate-200'} hover:bg-black/5`}
          >
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </div>
        {expanded && (
          <div className="ml-6 mt-1 space-y-0.5 border-l-2 border-slate-200 pl-3" data-testid={`nav-${item.name.toLowerCase()}-submenu`}>
            {PRODUCT_CATEGORIES.map((cat) => {
              const linkTo = `${item.path}?category=${cat.key}`;
              const active = isActive && currentCat === cat.key;
              return (
                <Link
                  key={cat.key}
                  to={linkTo}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`nav-${item.name.toLowerCase()}-${cat.key.replace(/:/g, '-')}`}
                  className={`block px-3 py-1.5 rounded text-xs ${
                    active ? 'bg-blue-50 text-blue-800 font-semibold' : 'text-slate-600 hover:text-blue-800 hover:bg-slate-50'
                  }`}
                >
                  {cat.label}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const NavContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b border-slate-200">
        <BrandLogo size={32} data-testid="app-logo" />
        <p className="text-sm text-slate-600 mt-1">Agent CRM with AI Brain.</p>
      </div>
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {topItems.map((item) => <NavLinkItem key={item.path} item={item} onClick={() => setMobileOpen(false)} />)}
        {submenus.map((item) => <SubMenu key={item.key} item={item} />)}
        {bottomItems.map((item) => <NavLinkItem key={item.path} item={item} onClick={() => setMobileOpen(false)} />)}
      </nav>
      <div className="p-4 border-t border-slate-200">
        <div className="flex items-center mb-3">
          <UserCircle className="h-8 w-8 text-slate-400 mr-3" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
        </div>
        <Button onClick={handleLogout} variant="outline" className="w-full border-slate-300 hover:bg-red-50 hover:border-red-300 hover:text-red-600" data-testid="logout-button">
          <LogOut className="h-4 w-4 mr-2" /> Logout
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-slate-50">
      <aside className="hidden lg:flex lg:w-64 lg:flex-col bg-white border-r border-slate-200">
        <NavContent />
      </aside>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild className="lg:hidden">
          <Button variant="ghost" size="icon" className="fixed top-3 left-3 z-40 bg-white border border-slate-200 shadow-sm" data-testid="mobile-menu-button">
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-64 p-0">
          <NavContent />
        </SheetContent>
      </Sheet>
      <div className="flex-1 flex flex-col min-w-0">
        <SubscriptionBanner />
        <header className="sticky top-0 z-30 flex items-center gap-3 h-16 px-4 lg:px-6 bg-white/90 backdrop-blur border-b border-slate-200" data-testid="app-topbar">
          <span className="hidden md:inline text-sm font-semibold text-slate-500 uppercase tracking-wider">{pageTitle}</span>
          <GlobalSearchBox />
          <div className="flex-1 md:hidden" />
          <Button onClick={openAddClient} variant="outline" className="hidden sm:inline-flex border-blue-700 text-blue-800 hover:bg-blue-50" data-testid="topbar-add-client">
            <Plus className="h-4 w-4 mr-1.5" /> Add Client
          </Button>
          <Button onClick={() => openAddProduct()} className="bg-blue-800 hover:bg-blue-900 text-white" data-testid="topbar-add-product">
            <Plus className="h-4 w-4 mr-1.5" /> Add Product
          </Button>
          <Link to="/notifications" className="ml-1 p-2 rounded-full hover:bg-slate-100 text-slate-600" data-testid="topbar-notifications" aria-label="Notifications">
            <Bell className="h-5 w-5" />
          </Link>
          <button
            type="button"
            onClick={() => setSupportOpen(true)}
            className="ml-1 inline-flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium rounded-md text-blue-800 hover:bg-blue-50 border border-transparent hover:border-blue-200"
            data-testid="topbar-support"
            aria-label="Contact Support"
          >
            <LifeBuoy className="h-4 w-4" />
            <span className="hidden md:inline">Support</span>
          </button>
          {/* Trial-end countdown — self-hides unless the user is a trial user
              with ≤3 days remaining. Sits in the top-right so it's glanceable
              from every page, not just the Dashboard. */}
          <TrialCountdownBadge />
          <div className="hidden sm:flex items-center gap-2 pl-2 border-l border-slate-200 ml-1">
            {user?.profile_photo ? (
              <img src={user.profile_photo.startsWith('http') ? user.profile_photo : `${process.env.REACT_APP_BACKEND_URL}${user.profile_photo}`} alt={user?.name} className="h-8 w-8 rounded-full object-cover border border-slate-200" data-testid="topbar-avatar" />
            ) : (
              <UserCircle className="h-7 w-7 text-slate-400" data-testid="topbar-avatar-placeholder" />
            )}
            <span className="text-sm font-medium text-slate-700 max-w-[120px] truncate">{user?.name}</span>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto p-4 lg:p-8">{children}</div>
        </main>
      </div>
      <SupportDialog open={supportOpen} onClose={() => setSupportOpen(false)} />
    </div>
  );
};

export default Layout;
